import { z } from "zod";

export const userRoleSchema = z.enum(["user", "support", "admin"]);

export const updateProfileSchema = z.object({
	firstName: z
		.string()
		.min(1, "First Name is required")
		.max(50, "First Name must be less than 50 characters"),
	lastName: z
		.string()
		.min(1, "Last Name is required")
		.max(50, "Last Name must be less than 50 characters"),
});

export const updatePasswordSchema = z
	.object({
		currentPassword: z.string().min(1, "Current Password is required"),
		newPassword: z
			.string()
			.min(8, "Password must be at least 8 characters")
			.max(128, "Password must be less than 128 characters"),
		confirmPassword: z.string().min(1, "Please confirm your password"),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

export const createUserSchema = z.object({
	email: z.string().email("Invalid email address").min(1, "Email is required"),
	password: z.string().min(8, "Password must be at least 8 characters").optional(),
	firstName: z
		.string()
		.min(1, "First Name is required")
		.max(50, "First Name must be less than 50 characters"),
	lastName: z
		.string()
		.min(1, "Last Name is required")
		.max(50, "Last Name must be less than 50 characters"),
	role: userRoleSchema,
});

export const bulkUserRowSchema = createUserSchema.pick({
	email: true,
	firstName: true,
	lastName: true,
});

export const bulkCreateUsersSchema = z.object({
	role: userRoleSchema,
	createAsTestUser: z.boolean(),
	users: z.array(bulkUserRowSchema).min(1).max(100),
});

export const activityUserSnapshotSchema = z.object({
	id: z.string().min(1),
	email: z.string().email(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	role: userRoleSchema,
	planId: z.enum(["free", "pro", "max"]),
	isTest: z.boolean(),
});

export const bulkUsersCreatedCompleteSchema = z
	.object({
		role: userRoleSchema,
		createAsTestUser: z.boolean(),
		totalRequested: z.number().int().min(1).max(100),
		createdCount: z.number().int().min(0).max(100),
		failedCount: z.number().int().min(0).max(100),
		failed: z.array(
			z.object({
				email: z.string(),
				error: z.string(),
				rowIndex: z.number().int().min(0).optional(),
				existingUserId: z.string().min(1).optional(),
			}),
		),
		createdUserIds: z.array(z.string().min(1)),
		createdUsers: z.array(activityUserSnapshotSchema).optional(),
	})
	.refine((d) => d.createdCount === (d.createdUsers?.length ?? d.createdUserIds.length), {
		message: "Created Count must match created users length",
		path: ["createdUsers"],
	})
	.refine((d) => d.failedCount === d.failed.length, {
		message: "Failed Count must match Failed entries length",
		path: ["failed"],
	})
	.refine((d) => d.createdCount + d.failedCount === d.totalRequested, {
		message: "Created Count plus Failed Count must equal Total Requested",
		path: ["totalRequested"],
	});

export const updateUserSchema = z
	.object({
		firstName: z.string().max(50, "First Name must be less than 50 characters").optional(),
		lastName: z.string().max(50, "Last Name must be less than 50 characters").optional(),
		role: userRoleSchema,
		planId: z.enum(["free", "pro"]).optional(),
		isTest: z.boolean().optional(),
	})
	.refine((data) => data.role === "admin" || data.role === "support" || data.planId !== undefined, {
		message: "Plan is required for non-admin users",
		path: ["planId"],
	});

export const forgotPasswordSchema = z.object({
	email: z.string().email("Invalid email address").min(1, "Email is required"),
});

export const resetPasswordSchema = z
	.object({
		password: z
			.string()
			.min(8, "Password must be at least 8 characters")
			.max(128, "Password must be less than 128 characters"),
		confirmPassword: z.string().min(1, "Please confirm your password"),
		token: z.string().min(1, "Reset token is required"),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

export const loginSchema = z.object({
	email: z.string().email("Invalid email address").min(1, "Email is required"),
	password: z.string().min(1, "Password is required"),
	rememberMe: z.boolean().optional().default(false),
});

export const passkeyNameSchema = z.object({
	name: z
		.string()
		.min(1, "Passkey Name is required")
		.max(100, "Passkey Name must be less than 100 characters"),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type BulkUserRowInput = z.infer<typeof bulkUserRowSchema>;
export type BulkCreateUsersInput = z.infer<typeof bulkCreateUsersSchema>;
export type BulkUsersCreatedCompleteInput = z.infer<typeof bulkUsersCreatedCompleteSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type PasskeyNameInput = z.infer<typeof passkeyNameSchema>;
